import { apiClient } from './client';

export interface SignageDisplay {
  id: string;
  tenant_id: string;
  branch_id: string;
  display_name: string;
  online: boolean;
  layout: string;
  zone_order: string[];
  zone_widths: Record<string, number>;
  cache_size_bytes: number;
  cache_max_bytes: number;
  last_heartbeat_at: string | null;
  last_event: string;
  ticker_config: Record<string, unknown>;
  audio_config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface SignageAnnouncement {
  id: string;
  tenant_id: string;
  branch_id: string;
  message: string;
  type: string;
  priority: string;
  scheduled_at: string | null;
  expires_at: string | null;
  active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface SignagePlaylist {
  id: string;
  tenant_id: string;
  branch_id: string;
  name: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SignagePlaylistItem {
  id: string;
  playlist_id: string;
  type: string;
  source_url: string;
  duration_sec: number;
  caption: string;
  display_order: number;
  created_at: string;
}

export interface SignageMenuConfig {
  id: string;
  tenant_id: string;
  branch_id: string;
  enabled_categories: string[];
  disabled_products: string[];
  manual_overrides: { product_id: string; forced_rank: number }[];
  rotation_interval_sec: number;
  max_products_per_category: number;
  created_at: string;
  updated_at: string;
}

export interface SignageDisplaySettings {
  id: string;
  tenant_id: string;
  branch_id: string;
  layout: string;
  zone_order: string[];
  zone_widths: Record<string, number>;
  ticker_enabled: boolean;
  ticker_channels: { channel: string; source: string; interval_sec: number }[];
  ticker_scroll_speed: number;
  audio_ducking_enabled: boolean;
  audio_background_gain: number;
  audio_ramp_ms: number;
  split_flap_enabled: boolean;
  split_flap_volume: number;
  display_mode: 'queue' | 'advertising' | 'hybrid';
  audio_output: 'usb' | 'hdmi';
  created_at: string;
  updated_at: string;
}

/**
 * A daypart schedule rule for a playlist.
 * days_of_week uses 0=Sunday … 6=Saturday (e.g. [1,2,3,4,5] = Mon–Fri).
 */
export interface SignageDaypartRule {
  id: string;
  playlist_id: string;
  days_of_week: number[];
  start_time: string; // "08:00"
  end_time: string; // "12:00"
  playlist_item_ids: string[];
}

/** Input shape for PUT /playlists/:id/schedule (server assigns id/playlist_id). */
export interface SignageDaypartRuleInput {
  days_of_week: number[];
  start_time: string;
  end_time: string;
  playlist_item_ids: string[];
}

export interface SignageBestsellerItem {
  product_id?: string;
  product_name?: string;
  name?: string;
  category?: string;
  price?: number;
  unit?: string;
  rank?: number;
  qty_sold?: number;
  [key: string]: unknown;
}

/** A real, admin-entered menu product (Phase 3 follow-up). */
export interface SignageMenuProduct {
  id: string;
  tenant_id: string;
  branch_id: string;
  category: string;
  name: string;
  price: number;
  unit: string;
  display_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateMenuProductDTO {
  category?: string;
  name: string;
  price?: number;
  unit?: string;
  display_order?: number;
}

/** Partial update — omitted fields are left untouched server-side. */
export interface UpdateMenuProductDTO {
  category?: string;
  name?: string;
  price?: number;
  unit?: string;
  display_order?: number;
  active?: boolean;
}

export interface SignageBestsellers {
  bestsellers: SignageBestsellerItem[];
  generated_at: string;
  source: string;
}

/**
 * Response of POST /playlists/:id/upload-url.
 * Backend contract: returns { url, key, public_url, expires_in } and fails
 * (non-2xx) when presign is unavailable. After a successful PUT to `url`,
 * store source_url = public_url (fallback to key only if public_url absent).
 */
export interface SignageUploadURL {
  url: string;
  key: string;
  public_url?: string;
  expires_in: number;
}

/** A device enrollment request awaiting admin approval. */
export interface SignageEnrollment {
  id: string;
  tenant_id: string;
  branch_id: string;
  fingerprint: string;
  hostname: string;
  device_id: string;
  site_id: string;
  status: string;
  display_name: string;
  created_at: string;
}

export interface ApproveEnrollmentDTO {
  display_name?: string;
  branch_id?: string;
}

export interface UpdateDisplayDTO {
  display_name?: string;
  branch_id?: string;
}

/** Response of GET /api/v1/version (public, no auth). */
export interface BackendVersionInfo {
  version: string;
  git_commit: string;
  build_time: string;
  go_version: string;
  timestamp: string;
}

export interface CreateAnnouncementDTO {
  message: string;
  type?: string;
  priority?: string;
  scheduled_at?: string;
  expires_at?: string;
  branch_id?: string;
}

export interface EmergencyAnnouncementDTO {
  message: string;
  branch_id?: string;
}

const BASE = '/api/v1/signage';

/**
 * Unwrap a response body. `apiClient` (fetch-based) resolves with the parsed
 * JSON body directly; this also tolerates an axios-style `{ data }` envelope
 * so the client keeps working if the transport changes.
 */
function unwrapBody<T>(res: unknown): T {
  if (
    res &&
    typeof res === 'object' &&
    !Array.isArray(res) &&
    'data' in (res as Record<string, unknown>)
  ) {
    return (res as { data: T }).data;
  }
  return res as T;
}

function withBranch(endpoint: string, branchId?: string): string {
  return branchId
    ? `${endpoint}?branch_id=${encodeURIComponent(branchId)}`
    : endpoint;
}

export const signageClient = {
  // Displays
  listDisplays: async (): Promise<SignageDisplay[]> => {
    const res = await apiClient.get(`${BASE}/displays`);
    return unwrapBody<{ displays?: SignageDisplay[] }>(res).displays || [];
  },

  getDisplay: async (id: string): Promise<SignageDisplay> => {
    const res = await apiClient.get(`${BASE}/displays/${id}`);
    return unwrapBody<SignageDisplay>(res);
  },

  updateDisplayConfig: async (
    id: string,
    config: Partial<SignageDisplay>
  ): Promise<SignageDisplay> => {
    const res = await apiClient.put(`${BASE}/displays/${id}/config`, config);
    return unwrapBody<SignageDisplay>(res);
  },

  restartDisplay: async (id: string): Promise<void> => {
    await apiClient.post(`${BASE}/displays/${id}/restart`);
  },

  getDisplayCache: async (
    id: string
  ): Promise<{
    cache_size_bytes: number;
    cache_max_bytes: number;
    usage_percent: number;
  }> => {
    const res = await apiClient.get(`${BASE}/displays/${id}/cache`);
    return unwrapBody<{
      cache_size_bytes: number;
      cache_max_bytes: number;
      usage_percent: number;
    }>(res);
  },

  clearDisplayCache: async (id: string): Promise<void> => {
    await apiClient.post(`${BASE}/displays/${id}/cache/clear`);
  },

  updateDisplay: async (
    id: string,
    data: UpdateDisplayDTO
  ): Promise<SignageDisplay> => {
    const res = await apiClient.put(`${BASE}/displays/${id}`, data);
    return unwrapBody<SignageDisplay>(res);
  },

  deleteDisplay: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}/displays/${id}`);
  },

  // Device enrollments
  listEnrollments: async (status = 'pending'): Promise<SignageEnrollment[]> => {
    const res = await apiClient.get(
      `${BASE}/enrollments?status=${encodeURIComponent(status)}`
    );
    return (
      unwrapBody<{ enrollments?: SignageEnrollment[] } | null | undefined>(res)
        ?.enrollments || []
    );
  },

  approveEnrollment: async (
    id: string,
    data: ApproveEnrollmentDTO = {}
  ): Promise<void> => {
    await apiClient.post(`${BASE}/enrollments/${id}/approve`, data);
  },

  rejectEnrollment: async (id: string): Promise<void> => {
    await apiClient.post(`${BASE}/enrollments/${id}/reject`);
  },

  // Version (public endpoint, no auth)
  getBackendVersion: async (): Promise<BackendVersionInfo> => {
    const res = await apiClient.get('/api/v1/version');
    return unwrapBody<BackendVersionInfo>(res);
  },

  // Announcements
  listAnnouncements: async (): Promise<SignageAnnouncement[]> => {
    const res = await apiClient.get(`${BASE}/announcements`);
    return (
      unwrapBody<{ announcements?: SignageAnnouncement[] }>(res)
        .announcements || []
    );
  },

  createAnnouncement: async (
    data: CreateAnnouncementDTO
  ): Promise<SignageAnnouncement> => {
    const res = await apiClient.post(`${BASE}/announcements`, data);
    return unwrapBody<SignageAnnouncement>(res);
  },

  updateAnnouncement: async (
    id: string,
    data: Partial<CreateAnnouncementDTO> & { active?: boolean }
  ): Promise<SignageAnnouncement> => {
    const res = await apiClient.put(`${BASE}/announcements/${id}`, data);
    return unwrapBody<SignageAnnouncement>(res);
  },

  deleteAnnouncement: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}/announcements/${id}`);
  },

  publishAnnouncement: async (id: string): Promise<void> => {
    await apiClient.post(`${BASE}/announcements/${id}/publish`);
  },

  emergencyAnnouncement: async (
    data: EmergencyAnnouncementDTO
  ): Promise<SignageAnnouncement> => {
    const res = await apiClient.post(`${BASE}/announcements/emergency`, data);
    return unwrapBody<SignageAnnouncement>(res);
  },

  clearEmergency: async (): Promise<void> => {
    await apiClient.delete(`${BASE}/announcements/emergency`);
  },

  // Playlists
  listPlaylists: async (): Promise<SignagePlaylist[]> => {
    const res = await apiClient.get(`${BASE}/playlists`);
    return unwrapBody<{ playlists?: SignagePlaylist[] }>(res).playlists || [];
  },

  createPlaylist: async (data: {
    name: string;
    branch_id?: string;
  }): Promise<SignagePlaylist> => {
    const res = await apiClient.post(`${BASE}/playlists`, data);
    return unwrapBody<SignagePlaylist>(res);
  },

  updatePlaylist: async (
    id: string,
    data: { name?: string; active?: boolean }
  ): Promise<SignagePlaylist> => {
    const res = await apiClient.put(`${BASE}/playlists/${id}`, data);
    return unwrapBody<SignagePlaylist>(res);
  },

  deletePlaylist: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}/playlists/${id}`);
  },

  addPlaylistItem: async (
    playlistId: string,
    data: {
      type?: string;
      source_url: string;
      duration_sec?: number;
      caption?: string;
      display_order?: number;
    }
  ): Promise<SignagePlaylistItem> => {
    const res = await apiClient.post(
      `${BASE}/playlists/${playlistId}/items`,
      data
    );
    return unwrapBody<SignagePlaylistItem>(res);
  },

  updatePlaylistItem: async (
    playlistId: string,
    itemId: string,
    data: { caption?: string; duration_sec?: number; display_order?: number }
  ): Promise<SignagePlaylistItem> => {
    const res = await apiClient.put(
      `${BASE}/playlists/${playlistId}/items/${itemId}`,
      data
    );
    return unwrapBody<SignagePlaylistItem>(res);
  },

  listPlaylistItems: async (
    playlistId: string
  ): Promise<SignagePlaylistItem[]> => {
    const res = await apiClient.get(`${BASE}/playlists/${playlistId}/items`);
    // The body is either a plain array or an { items: [...] } wrapper,
    // optionally inside an axios-style { data } envelope.
    const body = unwrapBody<unknown>(res);
    if (Array.isArray(body)) return body as SignagePlaylistItem[];
    const wrapped = body as
      { items?: SignagePlaylistItem[] } | null | undefined;
    if (wrapped && Array.isArray(wrapped.items)) {
      return wrapped.items;
    }
    return [];
  },

  removePlaylistItem: async (
    playlistId: string,
    itemId: string
  ): Promise<void> => {
    await apiClient.delete(`${BASE}/playlists/${playlistId}/items/${itemId}`);
  },

  getPlaylistSchedule: async (
    playlistId: string
  ): Promise<SignageDaypartRule[]> => {
    const res = await apiClient.get(`${BASE}/playlists/${playlistId}/schedule`);
    return (
      unwrapBody<{ schedule?: SignageDaypartRule[] } | null | undefined>(res)
        ?.schedule || []
    );
  },

  updatePlaylistSchedule: async (
    playlistId: string,
    schedule: SignageDaypartRuleInput[]
  ): Promise<SignageDaypartRule[]> => {
    const res = await apiClient.put(
      `${BASE}/playlists/${playlistId}/schedule`,
      { schedule }
    );
    return (
      unwrapBody<{ schedule?: SignageDaypartRule[] } | null | undefined>(res)
        ?.schedule || []
    );
  },

  publishPlaylist: async (id: string): Promise<void> => {
    await apiClient.post(`${BASE}/playlists/${id}/publish`);
  },

  getUploadURL: async (playlistId: string): Promise<SignageUploadURL> => {
    const res = await apiClient.post(
      `${BASE}/playlists/${playlistId}/upload-url`
    );
    return unwrapBody<SignageUploadURL>(res);
  },

  // Menu Config
  getMenuConfig: async (branchId?: string): Promise<SignageMenuConfig> => {
    const res = await apiClient.get(`${BASE}/menu/config`, {
      params: { branch_id: branchId },
    });
    return unwrapBody<SignageMenuConfig>(res);
  },

  updateMenuConfig: async (
    data: Partial<SignageMenuConfig>,
    branchId?: string
  ): Promise<SignageMenuConfig> => {
    const res = await apiClient.put(
      withBranch(`${BASE}/menu/config`, branchId),
      data
    );
    return unwrapBody<SignageMenuConfig>(res);
  },

  getMenuBestsellers: async (
    branchId?: string
  ): Promise<SignageBestsellers> => {
    const res = await apiClient.get(`${BASE}/menu/bestsellers`, {
      params: { branch_id: branchId },
    });
    const body = unwrapBody<Partial<SignageBestsellers> | null | undefined>(
      res
    );
    return {
      bestsellers: Array.isArray(body?.bestsellers) ? body!.bestsellers! : [],
      generated_at: body?.generated_at || '',
      source: body?.source || 'none',
    };
  },

  // Menu Products (Phase 3 follow-up — real admin-entered catalog)
  listMenuProducts: async (
    branchId?: string
  ): Promise<SignageMenuProduct[]> => {
    const res = await apiClient.get(`${BASE}/menu/products`, {
      params: { branch_id: branchId },
    });
    return (
      unwrapBody<{ products?: SignageMenuProduct[] } | null | undefined>(res)
        ?.products || []
    );
  },

  createMenuProduct: async (
    data: CreateMenuProductDTO,
    branchId?: string
  ): Promise<SignageMenuProduct> => {
    const res = await apiClient.post(
      withBranch(`${BASE}/menu/products`, branchId),
      data
    );
    return unwrapBody<SignageMenuProduct>(res);
  },

  updateMenuProduct: async (
    id: string,
    data: UpdateMenuProductDTO
  ): Promise<SignageMenuProduct> => {
    const res = await apiClient.put(`${BASE}/menu/products/${id}`, data);
    return unwrapBody<SignageMenuProduct>(res);
  },

  deleteMenuProduct: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}/menu/products/${id}`);
  },

  // Settings
  getDisplaySettings: async (
    branchId?: string
  ): Promise<SignageDisplaySettings> => {
    const res = await apiClient.get(`${BASE}/settings`, {
      params: { branch_id: branchId },
    });
    return unwrapBody<SignageDisplaySettings>(res);
  },

  updateDisplaySettings: async (
    data: Partial<SignageDisplaySettings>,
    branchId?: string
  ): Promise<SignageDisplaySettings> => {
    const res = await apiClient.put(
      withBranch(`${BASE}/settings`, branchId),
      data
    );
    return unwrapBody<SignageDisplaySettings>(res);
  },
};
